import NiceModal, { NiceModalHocProps } from "@ebay/nice-modal-react";
import { Divider, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { ApiClientAxios as Axios } from "@repo/api";
import { InfiniteData, UseInfiniteQueryResult } from "@repo/logic";
import { type FC, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatDate, NumberToCZK } from "../../helpers";
import { useIsDesktop, useTypedTranslation } from "../../hooks";
import { InternalLink } from "../InternalLink";
import { ModalSlideUp, ModalWith2Buttons, ModalWith2ButtonsProps } from "../Modals";
import { BodyTextM, BodyTextS, HeadingM } from "../Typography";
import * as Styled from "./TransactionsTab.styled";
import { TransactionsTabPremiumScheduleFilterModal } from "./TransactionsTabPremiumScheduleFilterModal";

type TransactionsTabPremiumScheduleProps = {
    premiumsQuery?: {
        query: UseInfiniteQueryResult<InfiniteData<Axios.FufiFinancialInfoResponse, unknown>, unknown>;
        flatData: Axios.FufiFinancialInfoPremium[];
    };
    filter?: PremiumScheduleFilter;
    onFilterChange?: (filter: PremiumScheduleFilter) => void;
    hidePaymentDate?: boolean;
};

export type PremiumScheduleFilter = {
    from: Date | null;
    to: Date | null;
};

const initialFilterState: PremiumScheduleFilter = {
    from: null,
    to: null,
};

export const TransactionsTabPremiumSchedule: FC<TransactionsTabPremiumScheduleProps> = ({
    premiumsQuery,
    filter,
    onFilterChange,
    hidePaymentDate,
}) => {
    const isDesktop = useIsDesktop();
    const { t } = useTypedTranslation();
    // eslint-disable-next-line @repo/internal/no-useTranslation
    const { t: t_UNSAFE } = useTranslation();

    const { flatData: data, query } = premiumsQuery || {};
    const { fetchNextPage, hasNextPage, isLoading } = query || {};

    const handleShowMore = useCallback(() => {
        hasNextPage && fetchNextPage?.();
    }, [fetchNextPage, hasNextPage]);

    const handleShowfilter = useCallback(
        async (ev: React.SyntheticEvent) => {
            ev.preventDefault();
            const result = await NiceModal.show<
                false | null | PremiumScheduleFilter,
                ModalWith2ButtonsProps & NiceModalHocProps,
                ModalWith2ButtonsProps
            >(ModalWith2Buttons, {
                width: 480,
                button1Text: t("fufi.regulationsFilters.cancel"),
                button2Text: t("fufi.regulationsFilters.accept"),
                title: t("fufi.regulationsFilters.title"),
                resetReturnPayloadOnClose: true,
                content: ({ setReturnPayload }) => (
                    <TransactionsTabPremiumScheduleFilterModal
                        setReturnPayload={setReturnPayload}
                        initialValues={filter ?? initialFilterState}
                    />
                ),
                components: {
                    DialogContentWrapperComponent: Styled.FilterDialogContent,
                },
            });

            if (typeof result === "object" && result !== null) {
                onFilterChange?.({ from: result.from, to: result.to });
            } else if (result === false) {
                onFilterChange?.({ ...initialFilterState });
            }
        },
        [filter, t, onFilterChange],
    );

    const LoadingComponent = memo(() => (
        <>
            {isLoading && (
                <TableRow>
                    <TableCell colSpan={7}>
                        <Skeleton width="100%" />
                    </TableCell>
                </TableRow>
            )}
        </>
    ));

    const handleShowDetail = useCallback(
        (transaction: Axios.FufiFinancialInfoPremium) => {
            return NiceModal.show(ModalSlideUp, {
                title: t("fufi.insuranceRegulationsTable.titles.detailTitle"),
                content: (
                    <Styled.StackWithGap divider={<Divider />}>
                        <Styled.StackSplitted>
                            <BodyTextS>{t("fufi.insuranceRegulationsTable.titles.description")}</BodyTextS>
                            <BodyTextS>
                                {t_UNSAFE(`fufi.insuranceRegulationsTable.status.${transaction.paymentStatus}`)}
                            </BodyTextS>
                        </Styled.StackSplitted>
                        <Styled.StackSplitted>
                            <BodyTextS>{t("fufi.insuranceRegulationsTable.titles.maturityDate")}</BodyTextS>
                            <BodyTextS>{`${formatDate(transaction.dueDate, "")}`}</BodyTextS>
                        </Styled.StackSplitted>
                        <Styled.StackSplitted>
                            <BodyTextS>{t("fufi.insuranceRegulationsTable.titles.state")}</BodyTextS>
                            <BodyTextS>
                                {t_UNSAFE(`fufi.insuranceRegulationsTable.status.${transaction.paymentStatus}`)}
                            </BodyTextS>
                        </Styled.StackSplitted>
                        {!hidePaymentDate && (
                            <Styled.StackSplitted>
                                <BodyTextS>{t("fufi.insuranceRegulationsTable.titles.paymentDate")}</BodyTextS>
                                <BodyTextS>{`${formatDate(transaction.paymentDate, "-")}`}</BodyTextS>
                            </Styled.StackSplitted>
                        )}
                        <Styled.StackSplitted>
                            <BodyTextS>{t("fufi.insuranceRegulationsTable.titles.insuracePaymentSum")}</BodyTextS>
                            <BodyTextS>{`${NumberToCZK(transaction.value)}`}</BodyTextS>
                        </Styled.StackSplitted>
                        <Styled.StackSplitted>
                            <BodyTextS>{t("fufi.insuranceRegulationsTable.titles.period")}</BodyTextS>
                            <BodyTextS>{`${formatDate(transaction.periodFrom, "")} - ${formatDate(transaction.periodTo, "")}`}</BodyTextS>
                        </Styled.StackSplitted>
                    </Styled.StackWithGap>
                ),
            });
        },
        [t, t_UNSAFE, hidePaymentDate],
    );

    return (
        <Stack>
            <Styled.StackBetween>
                <HeadingM>{t("fufi.insuranceRegulationsOverview.title")}</HeadingM>
                <InternalLink href="/" onClick={handleShowfilter}>
                    {t("fufi.regulationsFilters.filter")}
                </InternalLink>
            </Styled.StackBetween>

            {isDesktop ? (
                <Table>
                    <TableHead>
                        <TableCell>{t("fufi.insuranceRegulationsTable.titles.description")}</TableCell>
                        <Styled.WrappedTableCell $size={200}>
                            {t("fufi.insuranceRegulationsTable.titles.period")}
                        </Styled.WrappedTableCell>
                        <Styled.WrappedTableCell $size={hidePaymentDate ? 130 : 100}>
                            {t("fufi.insuranceRegulationsTable.titles.insuracePaymentSum")}
                        </Styled.WrappedTableCell>
                        <Styled.WrappedTableCell $size={hidePaymentDate ? 130 : 100}>
                            {t("fufi.insuranceRegulationsTable.titles.remainingToPay")}
                        </Styled.WrappedTableCell>
                        <Styled.WrappedTableCell $size={hidePaymentDate ? 130 : 100}>
                            {t("fufi.insuranceRegulationsTable.titles.maturityDate")}
                        </Styled.WrappedTableCell>
                        {!hidePaymentDate && (
                            <Styled.WrappedTableCell $size={100}>
                                {t("fufi.insuranceRegulationsTable.titles.paymentDate")}
                            </Styled.WrappedTableCell>
                        )}
                        <Styled.WrappedTableCell>
                            {t("fufi.insuranceRegulationsTable.titles.state")}
                        </Styled.WrappedTableCell>
                    </TableHead>
                    <TableBody>
                        {data?.map((transaction) => {
                            const key = crypto.randomUUID();
                            return (
                                <TableRow key={key}>
                                    <Styled.LeftTableCell>
                                        {t_UNSAFE(`fufi.insuranceRegulationsTable.frequency.${transaction.frequency}`)}
                                    </Styled.LeftTableCell>
                                    <Styled.RightTableCell>{`${formatDate(transaction.periodFrom, "")} - ${formatDate(transaction.periodTo, "")}`}</Styled.RightTableCell>
                                    <Styled.RightTableCell>{`${NumberToCZK(transaction.value)}`}</Styled.RightTableCell>
                                    <Styled.RightTableCell>{`${NumberToCZK(transaction.balance)}`}</Styled.RightTableCell>
                                    <Styled.RightTableCell>{`${formatDate(transaction.dueDate, "")}`}</Styled.RightTableCell>
                                    {!hidePaymentDate && (
                                        <Styled.RightTableCell>{`${formatDate(transaction.paymentDate, "-")}`}</Styled.RightTableCell>
                                    )}
                                    <Styled.RightTableCell>
                                        {t_UNSAFE(`fufi.insuranceRegulationsTable.status.${transaction.paymentStatus}`)}
                                    </Styled.RightTableCell>
                                </TableRow>
                            );
                        })}
                        {data?.length === 0 && !isLoading && (
                            <BodyTextM>{t("life.transactions.noTransactions")}</BodyTextM>
                        )}
                        <LoadingComponent />
                    </TableBody>
                </Table>
            ) : (
                <Table>
                    <TableHead>
                        <TableCell>{t("fufi.insuranceRegulationsTable.titles.maturityDate")}</TableCell>
                        <TableCell>{t("fufi.insuranceRegulationsTable.titles.description")}</TableCell>
                        <TableCell> </TableCell>
                    </TableHead>
                    <TableBody>
                        {data?.map((transaction) => {
                            const key = crypto.randomUUID();
                            return (
                                <TableRow key={key} onClick={() => handleShowDetail(transaction)}>
                                    <TableCell>{`${formatDate(transaction.dueDate, "")}`}</TableCell>
                                    <TableCell>
                                        {t_UNSAFE(`fufi.insuranceRegulationsTable.frequency.${transaction.frequency}`)}
                                    </TableCell>
                                    <TableCell>
                                        <Styled.SelectArrowOrange />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        <LoadingComponent />
                    </TableBody>
                </Table>
            )}

            <Styled.StackBetween>
                <Styled.TransactionTriggerArrow onClick={handleShowMore} disabled={hasNextPage === false}>
                    {t("fufi.insuranceRegulationsOverview.olderPayments")}
                </Styled.TransactionTriggerArrow>
            </Styled.StackBetween>
        </Stack>
    );
};
